import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import getConfig from 'next/config';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Button from '@havenengineering/module-shared-library/dist/components/Button/Button';
import { Size, Theme } from '@havenengineering/module-shared-library/dist/components/Button/Button';

import { ClientContext } from '../../helpers/clientContext';
import { errors, initialErrorState } from '../../helpers/errors';
import { FetchError, fetchWrapper, withIdentityApiUrl } from '../../helpers/fetch';
import { failedInteraction, formFieldError, successfulLoginInteraction } from '../../helpers/gtm';
import { weakEmailValidator } from '../../helpers/validator';
import { ControlledPasswordInput } from '../ControlledInputs/ControlledPasswordInput';
import { ControlledTextInput } from '../ControlledInputs/ControlledTextInput';
import { FormError } from '../FormError/FormError';
import styles from './Form.module.scss';
import { loginFormInitialValues, LoginFormValues } from './utils';

const {
  publicRuntimeConfig: { RECAPTCHA_SITE_KEY },
} = getConfig() as {
  publicRuntimeConfig: {
    RECAPTCHA_SITE_KEY: string;
  };
};

declare global {
  interface Window {
    recaptchaOptions: {
      useRecaptchaNet?: boolean;
      enterprise?: boolean;
    };
  }
}
if (typeof window !== 'undefined') {
  window.recaptchaOptions = {
    useRecaptchaNet: false,
    enterprise: true,
  };
}

type LoginResponse = {
  redirectTo: string;
  hid: string;
  isOwner: boolean;
};

type LoginFormProps = {
  clientContext: ClientContext;
  disabled?: boolean;
};

export const LoginForm = ({ clientContext, disabled = false }: LoginFormProps) => {
  const router = useRouter();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [loading, setLoading] = useState(disabled);
  const [error, setError] = useState(initialErrorState);
  const [captchaIsRequired, setCaptchaIsRequired] = useState(false);
  // spam protection
  const [accountName, setAccountName] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors: formStateErrors },
  } = useForm({
    defaultValues: loginFormInitialValues,
    mode: 'onTouched',
  });

  const onSubmit: SubmitHandler<LoginFormValues> = async (data) => {
    await login(data);
  };

  const onError: SubmitErrorHandler<LoginFormValues> = (errors) => {
    formFieldError('Log in', errors);
    setError(initialErrorState);
  };

  const login = async ({ email, password }: LoginFormValues) => {
    const digest = recaptchaRef.current ? recaptchaRef.current.getValue() : '';
    if (captchaIsRequired && !digest) {
      setError({
        message: 'Please complete the captcha',
        showCallCenter: false,
        showResetPassword: false,
        clientId: clientContext.id,
      });
      return;
    }

    try {
      setLoading(true);
      const body = {
        email,
        password,
        digest,
        ...(accountName ? { accountName } : {}),
      };
      const { redirectTo, hid, isOwner } = await fetchWrapper<LoginResponse>(
        withIdentityApiUrl('/interaction/login'),
        {
          method: 'POST',
          body: JSON.stringify(body),
          credentials: 'include',
        },
      );
      if (redirectTo.includes('upgrade')) {
        await router.push(redirectTo);
        return;
      }
      successfulLoginInteraction(hid, redirectTo, clientContext.id, isOwner);
    } catch (error) {
      setLoading(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      if (error instanceof FetchError) {
        if (error.accountRestricted) {
          await router.push('/account-restricted');
          return;
        }
        setCaptchaIsRequired(error.showCaptcha);
      }

      const finalError =
        error instanceof FetchError
          ? {
              message: error.message,
              showCallCenter: error.showCallCenter,
              showResetPassword: error.showResetPassword,
              clientId: clientContext.id,
            }
          : {
              ...errors.defaultError,
              clientId: clientContext.id,
            };

      failedInteraction('Log in', finalError.message);
      setError(finalError);
    }
  };

  return (
    <form
      autoComplete="off"
      method="post"
      id="signin-form"
      /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
      onSubmit={handleSubmit(onSubmit, onError)}
      className={styles.form}
    >
      <ControlledTextInput
        id="email"
        label="Email address"
        type="email"
        control={control}
        rules={{
          required: errors.requiredField.message,
          validate: weakEmailValidator,
        }}
        errors={formStateErrors}
      />

      <ControlledPasswordInput
        id="password"
        label="Password"
        control={control}
        rules={{ required: errors.requiredField.message }}
        errors={formStateErrors}
        autoComplete="current-password"
      />

      <label htmlFor="textInputaccountName" aria-hidden className={styles.required}>
        <input
          className={styles.required}
          id="textInputaccountName"
          type="text"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          autoComplete="off"
          tabIndex={-1}
        />
      </label>

      {captchaIsRequired && (
        <div className={styles.recaptchaContainer} data-testid="captcha-container">
          <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} ref={recaptchaRef} />
        </div>
      )}

      {error.message && <FormError error={error} />}

      <Link id="forgot-password-link" className={styles.forgotPasswordLink} href="/forgot-password">
        Forgot password?
      </Link>

      <Button
        id={'login'}
        className={styles.fullWidthButton}
        theme={Theme.PRIMARY_THEME}
        scale={Size.LARGE_SIZE}
        loading={loading}
        type="submit"
      >
        Log in
      </Button>
    </form>
  );
};
